/* eslint-disable import/extensions */
import * as entities from 'entities';
import * as Collections from 'typescript-collections';
import * as am5 from '@amcharts/amcharts5';
import * as am5map from '@amcharts/amcharts5/map';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import {type BrsData} from '@unbrs/plastic-data';
import {type ICountry, type IProject} from '@unbrs/plastic-data/src/brs';
import {type IRegionUN} from '@unbrs/plastic-data/src/types/project';
import {BrsBaseWidget} from '../base';
import {BrsPlasticProjectsWidget} from './projects';

BrsBaseWidget.registerBrsWidget(BrsPlasticProjectsWidget);

export type BrsPlasticMapWidgetOptions = {
	dataSource: kendo.data.DataSource;
	geojson?: GeoJSON.FeatureCollection;
	developments?: string[];
	regions?: IRegionUN[];
};

export class BrsPlasticMapWidget extends BrsBaseWidget {
	private readonly polygonSeries: am5map.MapPolygonSeries;
	private readonly heatLegend: am5.HeatLegend;
	private readonly dataSource: kendo.data.DataSource;
	private readonly popup: kendo.ui.Window;
	private readonly disclamerWindow: kendo.ui.Window;
	private readonly projectsWidget: BrsPlasticProjectsWidget;

	constructor(selector: Element, options?: BrsPlasticMapWidgetOptions) {
		super(selector, options);
		this.dataSource = kendo.data.DataSource.create(this.options.dataSource as kendo.data.DataSourceOptions);
		this.dataSource.bind('change', this.refresh.bind(this));
		if (this.element.length === 0) {
			console.log('Element not found', selector);
		}

		const root = am5.Root.new(this.element[0]!);
		root.setThemes([am5themes_Animated.new(root)]);
		// Create the map chart
		const chart = root.container.children.push(
			am5map.MapChart.new(root, {
				projection: am5map.geoEqualEarth(),
				panX: 'rotateX',
				panY: 'translateY',
				layout: root.horizontalLayout,
			}),
		);
		const zoomControl = chart.set(
			'zoomControl',
			am5map.ZoomControl.new(root, {
				x: am5.percent(7),
				y: am5.percent(25),
			}),
		);
		zoomControl.homeButton.set('visible', true);

		this.disclamerWindow = $('#brs-disclaimer-popup').kendoWindow({
			title: 'Disclaimer',
			width: '500px',
			height: '200px',
			visible: false, // Initially hidden
		}).data('kendoWindow')!;

		const button = am5.Button.new(root, {
			x: am5.percent(6),
			y: am5.percent(94),
			label: am5.Label.new(root, {
				text: 'Disclaimer',
				fill: am5.color(0x00_00_00),
				fillOpacity: 0.5,
				textDecoration: 'underline',
			}),
		});

		button.get('background')?.setAll({
			fillOpacity: 0,
			strokeOpacity: 0,
		});

		button.events.on('pointerover', event => {
			const rect = chart.root.dom.getBoundingClientRect();
			if (this.disclamerWindow.wrapper) {
				this.disclamerWindow.wrapper.css({
					top: rect.top + event.point.y - (this.disclamerWindow.wrapper?.height() ?? 0),
					left: rect.left + event.point.x,
				});
				this.disclamerWindow.open();
			}
		});
		button.events.on('pointerout', () => {
			this.disclamerWindow.close();
		});
		chart.children.push(button);

		const polygonSeries = chart.series.push(
			am5map.MapPolygonSeries.new(root, {
				geoJSON: options?.geojson,
				valueField: 'countProjectsPerCountry',
				calculateAggregates: true,
				exclude: ['AQ'],
			}),
		);
		console.log('GeoJSON', options?.geojson);
		polygonSeries.set('reverseGeodata', true);
		polygonSeries.set('heatRules', [
			{
				target: polygonSeries.mapPolygons.template,
				dataField: 'value',
				key: 'fill',
			},
		]);

		this.popup = $('#brs-map-popup').kendoWindow({
			width: '600px',
			height: '400px',
			visible: false, // Initially hidden
		}).data('kendoWindow')!;
		this.projectsWidget = $('#brs-map-projects').kendoBrsPlasticProjectsWidget(
			{pageable: false},
		).data('kendoBrsPlasticProjectsWidget');
		polygonSeries.mapPolygons.template.events.on('click', (event: any) => {
			if (
				!event.target.dataItem?.dataContext.projects
			) {
				return;
			}

			const dataContext = event.target.dataItem.dataContext; // eslint-disable-line @typescript-eslint/no-unsafe-assignment
			const rect = chart.root.dom.getBoundingClientRect();

			if (this.popup.wrapper) {
				let winTop: number = rect.top + event.point.y; // eslint-disable-line @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unsafe-assignment
				let winLeft: number = rect.left + event.point.x; // eslint-disable-line @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unsafe-assignment
				const popupHeight = this.popup.wrapper.height();
				const popupWidth = this.popup.wrapper.width();
				if (popupHeight && popupWidth) {
					if (winTop + popupHeight > rect.bottom) {
						winTop = rect.bottom - popupHeight;
					}

					if (winLeft + popupWidth > rect.right) {
						winLeft = rect.right - popupWidth;
					}

					this.popup.wrapper.css({
						top: winTop,
						left: winLeft,
					});
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const numberProjects: number = dataContext.projects.length;
					this.projectsWidget.setDataSource(event.target.dataItem?.dataContext.projects as IProject[]);
					this.popup.title((dataContext.name as string) + ' — ' + numberProjects + ' project' + (numberProjects > 1 ? 's' : ''));
					this.popup.open();
				}
			}
		});
		// eslint-enable no-any
		const heatLegend = chart.children.push(
			am5.HeatLegend.new(root, {
				orientation: 'horizontal',
				y: am5.percent(90),
				x: am5.percent(50),
				startColor: am5.color(0xFF_AA_FF),
				endColor: am5.color(0xAA_00_FF),
				endText: 'Projects per country',
				stepCount: 5,
			}),
		);

		heatLegend.startLabel.setAll({
			fontSize: 12,
			fill: heatLegend.get('startColor'),
		});

		heatLegend.endLabel.setAll({
			fontSize: 12,
			fill: heatLegend.get('endColor'),
		});
		polygonSeries.mapPolygons.template.set('tooltipPosition', 'pointer');
		polygonSeries.mapPolygons.template.events.on(
			'pointerover',
			(event: am5.ISpritePointerEvent) => {
				const dataItem = event.target.dataItem;
				if (dataItem) {
					const value = dataItem.get(('value' as any)) as number;
					heatLegend.showValue(value);
				}
			},
		);

		// Change this to template when possible
		polygonSeries.events.on('datavalidated', () => {
			const valueLow = polygonSeries.getPrivate('valueLow');
			const valueHigh = polygonSeries.getPrivate('valueHigh');
			if (valueLow === undefined || valueHigh === undefined) {
				return;
			}

			heatLegend.set('startValue', valueLow);
			heatLegend.set(
				'endValue',
				valueHigh === valueLow ? valueLow + 1 : valueHigh,
			);
		});

		heatLegend.startLabel.setAll({
			fontSize: 12,
			fill: heatLegend.get('startColor'),
		});

		heatLegend.endLabel.setAll({
			fontSize: 12,
			fill: heatLegend.get('endColor'),
		});

		this.heatLegend = heatLegend;
		this.polygonSeries = polygonSeries;
	}

	refresh(event: {items: BrsData.IProject[]}) {
		this.updateData(event.items);
	}

	zoomToCountry(iso2: string) {
		const result = this.polygonSeries.getDataItemById(iso2);
		if (result) {
			this.polygonSeries.zoomToDataItem(result);
			// Apply highlight state
			const polygon = result.get('mapPolygon');
			const originalFill = polygon.get('fill');
			polygon.animate({key: 'fill', to: am5.color(0xFF_00_00), duration: 1000}); // Example highlight fill color

			setTimeout(() => {
				polygon.animate({key: 'fill', to: originalFill, duration: 1000}); // Example highlight fill color
			}, 1000);
		}
	}

	setBudgetMap() {
		console.log('Setting budget map');
		this.polygonSeries.set('valueField', 'budget');
		const heatRules = this.polygonSeries.get('heatRules');
		if (heatRules?.[0] !== undefined) {
			heatRules[0].min = am5.color(0x00_FF_00);
			heatRules[0].max = am5.color(0xFF_00_00);
			this.polygonSeries.set('heatRules', heatRules);
		}

		this.polygonSeries.mapPolygons.template.adapters.add(
			'tooltipText',
			(tooltipText, target) => {
				if (target.dataItem) {
					const value = target.dataItem.get(('value' as any)) as number;
					const result = '{name}' + (value > 0 ? ': {value} USD' : '');
					return result;
				}

				return tooltipText;
			},
		);
		this.heatLegend.set('startColor', am5.color(0x00_FF_00));
		this.heatLegend.set('endColor', am5.color(0xFF_00_00));
		this.heatLegend.set('endText', 'Budget per country');
		this.heatLegend.set('stepCount', 20);
	}

	setProjecsCountMap() {
		console.log('Setting projects count map');

		const START_COLOR = am5.color(0x35_50_C8);
		const END_COLOR = am5.color(0x00_00_77);

		this.polygonSeries.set('valueField', 'countProjectsPerCountry');
		const heatRules = this.polygonSeries.get('heatRules');
		if (heatRules?.[0] !== undefined) {
			heatRules[0].min = START_COLOR;
			heatRules[0].max = END_COLOR;
			this.polygonSeries.set('heatRules', heatRules);
		}

		this.polygonSeries.mapPolygons.template.adapters.add(
			'tooltipText',
			(tooltipText, target) => {
				if (target.dataItem) {
					const value = target.dataItem.get(('value' as any)) as number;
					const result
            = '{name}'
            + (value > 0 ? `: {value} project${value > 1 ? 's' : ''}` : '');
					return result;
				}

				return tooltipText;
			},
		);
		this.heatLegend.set('startColor', START_COLOR);
		this.heatLegend.set('endColor', END_COLOR);

		this.heatLegend.set('endText', 'Projects per country');
		this.heatLegend.set('stepCount', 5);
	}

	setDevelopments(values: string[]) {
		this.options.developments = values;
	}

	setRegions(regions: IRegionUN[]) {
		this.options.regions = regions;
	}

	updateData(data: BrsData.IProject[]) {
		const options = this.options as BrsPlasticMapWidgetOptions;
		const projectsPerCountry = new Collections.DefaultDictionary<
		string,
		BrsData.IProject[]
		>(() => []);

		const developmentMap: Record<string, keyof ICountry> = {
			Developing: 'isDeveloping',
			LDC: 'isLDC',
			SIDS: 'isSIDS',
		};

		for (const item of data) {
			item.description = item.description
				? entities.decodeHTML(item.description)
				: '';
			for (const beneficiary of item.beneficiaries) {
				let isAddBeneficiary = false;
				if (options.developments && options.developments.length > 0) {
					for (const value of (options.developments)) {
						const mapping = developmentMap[value];
						// eslint-disable-next-line max-depth
						if (mapping === undefined) {
							break;
						}

						// eslint-disable-next-line max-depth
						if (beneficiary[mapping]) {
							isAddBeneficiary = true;
							break;
						}
					}
				} else {
					isAddBeneficiary = true;
				}

				if (isAddBeneficiary && options.regions && options.regions.length > 0) {
					isAddBeneficiary = false;
					if (options.regions.map(x => x).includes(beneficiary.countryRegionUn)) {
						isAddBeneficiary = true;
					}
				}

				if (isAddBeneficiary) {
					projectsPerCountry.getValue(beneficiary.iso2).push(item);
				}
			}
		}

		const newData: any = projectsPerCountry.keys().map((key: string) => {
			const projects = projectsPerCountry.getValue(key);
			const budget = projects.reduce(
				(accumulator: number, project: BrsData.IProject) => accumulator + (project.budget ?? 0),
				0,
			);
			return {
				id: key,
				countProjectsPerCountry: projects.length,
				budget,
				projects,
			};
		});
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		this.polygonSeries.data.setAll(newData);
		this.polygonSeries.set('reverseGeodata', false);
	}
}

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface JQuery {
		kendoBrsPlasticMapWidget(options?: BrsPlasticMapWidgetOptions): JQuery;
		data(widgetName: 'kendoBrsPlasticMapWidget'): BrsPlasticMapWidget;
	}
}

BrsBaseWidget.registerBrsWidget(BrsPlasticMapWidget, 'BrsPlasticMapWidget');
kendo.ui.plugin(BrsPlasticMapWidget);
